<template>
  <el-form
    class="form form__sign_up"
    ref="form"
    :model="signUpForm"
    :rules="rules"
    @submit.native.prevent="onSubmit()"
    v-loading="!!loading"
  >

    <input-language-dropdown
      id="language"
      :disabled="!isOnline"
      :value="signUpForm.language"
      @input="setLanguage"
      @change="setLanguage"
    />

    <input-first-name-field
      id="firstName"
      :disabled="!isOnline"
      v-model="signUpForm.firstName"
    />

    <input-last-name-field
      id="lastName"
      :disabled="!isOnline"
      v-model="signUpForm.lastName"
    />

    <input-field
      id="dealerNumber"
      type="text"
      :label="$t('authenticationDealerNumberLabel')"
      :disabled="!isOnline"
      v-model="dealerNumberUpper"
    >
      <template v-slot:accessory>
        <router-link :to="{ name: 'Help' }" target="_blank">
          {{$t('authenticationDealerNumberAccessory')}}
        </router-link>
      </template>
    </input-field>

    <input-email-field
      id="email"
      :disabled="!isOnline"
      v-model="signUpForm.email"
    />

    <input-password-field
      id="password"
      :description="$t('authenticationPasswordDescription')"
      :disabled="!isOnline"
      v-model="signUpForm.password"
    />

    <input-checkbox
      id="legalAcceptance"
      :disabled="!isOnline"
      :label="$t('authenticationAcceptLegalLabel')"
      v-model="signUpForm.legalAcceptance"
    >
      <template v-slot:accessory>
        <a :href="cookiesUrl" target="_blank">{{$t('authenticationLegalLinkCookies')}}</a>,
        <a :href="privacyUrl" target="_blank">{{$t('authenticationLegalLinkPrivacy')}}</a>,
        <a :href="termsUrl" target="_blank">{{$t('authenticationLegalLinkTerms')}}</a>
      </template>
    </input-checkbox>

    <messages :messages="messages" :request="request" :customErrorHandler="customErrorHandler"></messages>

    <div class="card__footer">
      <div class="card__footer_button_wrap">
        <el-button
          type="primary"
          native-type="submit"
          :disabled="!canSubmitForm"
        >
          {{$t('applicationSignUp')}}
        </el-button>
      </div>
      <p class="card__footer_footnote">
        <span>{{$t('applicationHaveAccount')}}</span>
        <router-link class="card__footer_link" :to="{ name: 'Home' }" exact>
          {{$t('applicationLogin')}}
        </router-link>
      </p>
    </div>

  </el-form>
</template>

<script>
  import authentication from '@/authentication';
  import i18n from '@/configured/vue-i18n';
  import validations from './inputs/validations';
  import InputField from './inputs/InputField.vue';
  import InputCheckbox from './inputs/InputCheckbox.vue';
  import InputLanguageDropdown from './inputs/InputLanguageDropdown.vue';
  import InputEmailField from './inputs/InputEmailField.vue';
  import InputPasswordField from './inputs/InputPasswordField.vue';
  import InputFirstNameField from './inputs/InputFirstNameField.vue';
  import InputLastNameField from './inputs/InputLastNameField.vue';
  import Messages from './Messages.vue';
  import offline from '@/offline';
  import { mapActions, mapGetters, mapState } from 'vuex';

  export default {
    name: 'SignUpForm',
    data() {
      return {
        messages: [],
        signUpForm: {
          dealerNumber: '',
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          legalAcceptance: false,
          language: this.$store.state[i18n.store.namespace].language || '',
        },
        rules: {
          dealerNumber: [{
            required: true,
            message: i18n.i18n.t('authenticationSignUpDealerRequired'),
          }],
          email: validations.rules().email,
          password: validations.rules().password,
          firstName: [{
            required: true,
            message: i18n.i18n.t('authenticationSignUpFirstNameRequired'),
          }],
          lastName: [{
            required: true,
            message: i18n.i18n.t('authenticationSignUpLastNameRequired'),
          }],
          legalAcceptance: [{
            required: true,
            message: i18n.i18n.t('authenticationSignUpAcceptLegalRequired')
          }],
        },
        request: null,
        loading: false,
      }
    },
    computed: {
      ...mapState(offline.store.namespace, [
        'isOnline'
      ]),
      dealerNumberUpper: {
        get() {
          return this.signUpForm.dealerNumber;
        },
        set(value) {
          this.signUpForm.dealerNumber = value
            ? String(value).toUpperCase()
            : '';
        },
      },
      canSubmitForm() {
        return !!(
          this.isOnline &&
          this.signUpForm.dealerNumber &&
          this.signUpForm.email &&
          this.signUpForm.password &&
          this.signUpForm.firstName &&
          this.signUpForm.lastName &&
          this.signUpForm.legalAcceptance
        )
      },
      baseUrl() {
        return window.location.origin;
      },
      cookiesUrl() {
        return `${this.baseUrl}/#/cookies`;
      },
      privacyUrl() {
        return `${this.baseUrl}/#/privacy`;
      },
      termsUrl() {
        return `${this.baseUrl}/#/terms`;
      }
    },
    methods: {
      ...mapActions(authentication.store.namespace, [
        'clearSignUp',
        'registerUser',
      ]),
      onSubmit() {
        this.messages.splice(0, this.messages.length);
        this.loading = true;

        this.request = this.registerUser({
          email        : this.signUpForm.email,
          password     : this.signUpForm.password,
          firstName    : this.signUpForm.firstName,
          lastName     : this.signUpForm.lastName,
          dealerNumber : this.signUpForm.dealerNumber,
          language     : this.signUpForm.language,
        })
          .then(this.checkForErrorResponse)
          .then(this.handleSuccess);
        this.request.then(()=>{ this.loading=false }, ()=>{ this.loading=false });
      },
      checkForErrorResponse(response) {
        // Server returns 200 and errors.
        if (response.errors) {
          throw response;
        }
      },
      handleSuccess() {
        this.messages = [];
        this.clearSignUp();
        this.password = '';
        this.$root.$emit('scrollTo', 0, 0, true);
        this.$emit('success', this.signUpForm.email);
      },
      customErrorHandler: (errors) => {
        if ('email' in errors) {
          return i18n.i18n.t('authenticationSignUpAlreadyRegistered');
        }
        return i18n.i18n.t('authenticationSignUpError');
      },
      setLanguage(language) {
        this.signUpForm.language = language;
        i18n.loadLanguageAsync(language);
      }
    },
    components: {
      InputField,
      InputCheckbox,
      InputLanguageDropdown,
      InputEmailField,
      InputPasswordField,
      InputFirstNameField,
      InputLastNameField,
      Messages
    }
  }
</script>

<style lang="scss" scoped>
  .el-button-group {
    width: 100%;

    .el-button {
      width: 50%;
    }
  }
</style>
